import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Users, Eye, Activity, Radio, RefreshCw, Instagram, CheckCircle2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { DemoBanner } from "@/components/DemoBanner";
import { MetricCard } from "@/components/MetricCard";
import { GrowthChart } from "@/components/GrowthChart";
import { MetricSkeleton, ChartSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatNumber, formatCompact, formatPct, formatDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

const RANGES = ["7", "30", "90", "365", "all"];

export function Dashboard() {
  const { t } = useT();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [range, setRange] = useState("30");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dashboard", range],
    queryFn: async () => (await api.get(`/dashboard?range=${range}`)).data,
    retry: false,
  });

  const connect = useMutation({
    mutationFn: async () => (await api.post("/instagram/connect")).data,
    onSuccess: () => { qc.invalidateQueries(); toast.success("Instagram collegato!"); },
  });

  const refresh = useMutation({
    mutationFn: async () => (await api.post("/instagram/refresh")).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Dati aggiornati"); },
  });

  const noAccount = isError && error?.response?.status === 404;

  if (noAccount) {
    return (
      <DashboardLayout>
        <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-orange-400">
            <Instagram className="h-8 w-8 text-white" />
          </div>
          <h2 className="font-display text-2xl font-bold">{t("dashboard.noAccountTitle")}</h2>
          <p className="mt-2 max-w-sm text-muted-foreground">{t("dashboard.noAccountDesc")}</p>
          <Button onClick={() => connect.mutate()} disabled={connect.isPending} data-testid="connect-btn"
            className="mt-6 rounded-xl bg-brand-500 hover:bg-brand-600 text-black">
            {connect.isPending ? t("common.loading") : t("dashboard.connect")}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {data?.data_source === "demo" && <DemoBanner />}

        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="dashboard-greeting">
              {t("dashboard.greeting", { name: user?.name?.split(" ")[0] || "" })}
            </h1>
            {data?.account && (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" /> {t("dashboard.connected")} @{data.account.username}
                </span>
                <span>{t("dashboard.lastUpdate")}: {formatDateTime(data.account.last_synced_at)}</span>
              </div>
            )}
          </div>
          <Button variant="outline" onClick={() => refresh.mutate()} disabled={refresh.isPending} data-testid="refresh-btn" className="rounded-xl">
            <RefreshCw className={`mr-2 h-4 w-4 ${refresh.isPending ? "animate-spin" : ""}`} /> {t("dashboard.refresh")}
          </Button>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => <MetricSkeleton key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard index={0} icon={Users} label={t("dashboard.currentFollowers")} value={formatNumber(data.current_followers)} delta={data.followers_pct} accent="brand" />
            <MetricCard index={1} icon={Eye} label={t("dashboard.totalViews")} value={formatCompact(data.metrics.views)} accent="emerald" />
            <MetricCard index={2} icon={Activity} label={t("dashboard.avgEngagement")} value={`${data.metrics.engagement_rate}%`} accent="amber" />
            <MetricCard index={3} icon={Radio} label={t("dashboard.totalReach")} value={formatCompact(data.metrics.reach)} accent="rose" />
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {isLoading ? <ChartSkeleton /> : (
              <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="font-display text-lg font-semibold">{t("dashboard.followerGrowth")}</h3>
                    <p className={`text-sm font-medium ${data.followers_net >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`} data-testid="follower-growth-text">
                      {data.followers_net >= 0 ? "+" : ""}{formatNumber(data.followers_net)} {t(`dashboard.rangeLabels.${data.clamped ? "30" : range}`)} ({formatPct(data.followers_pct)})
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1 rounded-xl bg-muted p-1">
                    {RANGES.map((r) => (
                      <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${range === r ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                        {t(`dashboard.ranges.${r}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <GrowthChart data={data.follower_history} />
              </div>
            )}
          </div>

          <div>
            {isLoading ? <ChartSkeleton /> : data.best_reel && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-border bg-card p-6 shadow-sm h-full">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <Trophy className="h-4 w-4" />
                  <span className="text-xs font-mono uppercase tracking-widest">{t("dashboard.bestContent")}</span>
                </div>
                <div className="mt-4 overflow-hidden rounded-xl">
                  <img src={data.best_reel.thumbnail_url} alt="" className="aspect-video w-full object-cover" />
                </div>
                <p className="mt-3 line-clamp-2 text-sm font-medium">{data.best_reel.hook}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">{t("dashboard.growthScore")}</div>
                    <div className="font-display text-3xl font-bold text-brand-700 dark:text-brand-400">{data.best_reel.growth_score}<span className="text-base text-muted-foreground">/100</span></div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">{t("common.views")}</div>
                    <div className="font-display text-xl font-bold">{formatCompact(data.best_reel.views)}</div>
                  </div>
                </div>
                <p className="mt-3 rounded-lg bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-300">{data.best_reel.explanation}</p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
